package fr.frux.navigation

import fr.frux.core.maps.Location
import fr.frux.core.maps.MapCameraPosition

/**
 * Sérialisation Route ↔ chemin d'URL pour le web (#299). Seules les pages de contenu
 * (partageables, rechargeables) ont un chemin : les écrans à état local (formulaires,
 * TreeList capturée depuis la carte) et les écrans de session (Auth, Welcome) retournent
 * null et laissent l'URL de l'écran dessous en place.
 *
 * Vocabulaire produit (ADR 0008/0014) : « arbre », « récolte », « cueilleur ».
 */
fun Route.toUrlPath(): String? =
    when (this) {
        is Route.TreeDetail -> "/arbre/$treeId"
        is Route.HarvestDetail -> "/recolte/$harvestId"
        is Route.UserProfile -> "/cueilleur/$userId"
        is Route.SiteDetail -> "/lieu/$siteId"
        else -> staticRoutePaths[this]
    }

/** Pages sans paramètre : une table plutôt qu'une branche par route, qui grossit à chaque écran. */
private val staticRoutePaths: Map<Route, String> =
    mapOf(
        Route.Map to "/",
        Route.Harvests to "/recoltes",
        Route.Profile to "/profil",
        Route.MyTrees to "/profil/arbres",
        Route.BlockedUsers to "/profil/comptes-masques",
        Route.Premium to "/profil/frux-plus",
        Route.Favorites to "/favoris",
        Route.Notifications to "/activite",
        Route.Settings to "/reglages",
        Route.NotificationPreferences to "/reglages/notifications",
    )

/**
 * Inverse de [Route.toUrlPath]. Chemin inconnu ou mal formé = null : l'appelant retombe
 * sur le comportement par défaut (carte), jamais d'erreur.
 */
fun parseUrlPath(path: String): Route? {
    val segments = path.split('/').filter { it.isNotEmpty() }
    return when (segments.size) {
        0 -> if (path.startsWith("/")) Route.Map else null
        1 -> singleSegmentRoutes[segments[0]]
        2 -> parseTwoSegments(segments[0], segments[1])
        else -> null
    }
}

private val singleSegmentRoutes: Map<String, Route> =
    mapOf(
        // Cible des liens des pages lisibles par les robots : la carte, centrée via la query.
        "carte" to Route.Map,
        "recoltes" to Route.Harvests,
        "profil" to Route.Profile,
        "favoris" to Route.Favorites,
        "activite" to Route.Notifications,
        "reglages" to Route.Settings,
    )

/**
 * Pages désignées par un id numérique. La fiche de lieu, plus récente, refuse aussi un id nul ou
 * négatif, qu'aucun lieu ne porte.
 */
private val idRoutes: Map<String, (Long) -> Route?> =
    mapOf(
        "arbre" to { id -> Route.TreeDetail(id) },
        "recolte" to { id -> Route.HarvestDetail(id) },
        "cueilleur" to { id -> Route.UserProfile(id) },
        "lieu" to { id -> Route.SiteDetail(id).takeIf { id > 0 } },
    )

private fun parseTwoSegments(
    head: String,
    tail: String,
): Route? {
    idRoutes[head]?.let { build -> return tail.toLongOrNull()?.let(build) }
    return when (head) {
        "profil" -> {
            when (tail) {
                "arbres" -> Route.MyTrees
                "comptes-masques" -> Route.BlockedUsers
                "frux-plus" -> Route.Premium
                else -> null
            }
        }

        "reglages" -> {
            Route.NotificationPreferences.takeIf { tail == "notifications" }
        }

        else -> {
            null
        }
    }
}

private const val MIN_LINK_ZOOM = 3f
private const val MAX_LINK_ZOOM = 20f

/**
 * Vue de carte portée par la query d'un lien `/carte?lat=&lon=&zoom=`. Null si un paramètre
 * manque, n'est pas un nombre ou vise un point qui n'existe pas : l'appelant retombe alors sur la
 * dernière vue mémorisée. Le zoom est borné plutôt que refusé, un lien trop zoomé reste utile.
 */
fun parseMapViewQuery(search: String): MapCameraPosition? {
    val params =
        search
            .removePrefix("?")
            .split('&')
            .filter { it.isNotEmpty() }
            .associate { it.substringBefore('=') to it.substringAfter('=', "") }
    val lat = params["lat"]?.toDoubleOrNull()
    val lon = params["lon"]?.toDoubleOrNull()
    val zoom = params["zoom"]?.toFloatOrNull()
    val valid =
        lat != null && lon != null && zoom != null &&
            lat in -90.0..90.0 && lon in -180.0..180.0 && !zoom.isNaN()
    return if (valid) {
        MapCameraPosition(Location(lat, lon), zoom.coerceIn(MIN_LINK_ZOOM, MAX_LINK_ZOOM))
    } else {
        null
    }
}
